import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LightboxButton from "../../components/lightbox/LightboxButton";
import type { Game } from "../../types/game";
import { useAdvanceMutation, useGetGameQuery, useGetPlayerQuery } from "../../reducers/apiSlice";
import convertErrorMessage from "../../utils/convertErrorMessage";
import LightboxSurface from "../../components/lightbox/LightboxSurface";
import GlassPanel from "../../components/lightbox/GlassPanel";
import { initialTypeface } from "../../components/lightbox/typefaces";
import CharacterReveal from "./CharacterReveal";
import PrivateUpdates from "./PrivateUpdates";
import { useVisiblePolling, useRefreshOnReturn } from "./useLiveRefresh";
import styles from "./GameInProgressView.module.scss";

const HOST_INSTRUCTIONS_URL = "https://puzzlestorage221.blob.core.windows.net/publictesting/Host%20Instructions.pdf";
const noop = () => undefined;
export interface GameInProgressViewProps { game: Game }

export default function GameInProgressView({ game }: GameInProgressViewProps) {
  const isHost = game.is_host === true;
  const hostNotPlaying = isHost && game.host_is_playing === false;
  const [advance, { isLoading: advancing }] = useAdvanceMutation();
  const { refetch, isFetching, isError: gameRefreshFailed } = useGetGameQuery(game.objectId);
  const pollingInterval = useVisiblePolling();
  const accessCode = hostNotPlaying ? null : game.my_character?.access_code;
  const { currentData: playerData, isFetching: checkingUpdates, isError: updatesFailed, refetch: refetchPlayer } = useGetPlayerQuery(accessCode ?? "", {
    skip: !accessCode, pollingInterval, refetchOnMountOrArgChange: true,
  });
  useRefreshOnReturn(refetchPlayer, Boolean(accessCode));
  // UI-DESIGN: a changed round also checks the private dossier, even before
  // the next poll. Never infer private text from the public player roster.
  useEffect(() => { if (accessCode) void refetchPlayer(); }, [game.current_round, accessCode, refetchPlayer]);
  const updates: string[] = Array.isArray(playerData?.round_updates)
    ? playerData.round_updates.filter((entry: unknown): entry is string => typeof entry === "string") : [];
  const refreshAll = async () => {
    await Promise.allSettled([refetch().unwrap(), ...(accessCode ? [refetchPlayer().unwrap()] : [])]);
  };
  const [confirmRound, setConfirmRound] = useState<number | null>(null);
  const [advancedRound, setAdvancedRound] = useState<number | null>(null);
  const [error, setError] = useState("");
  const currentRound = game.current_round;
  const totalRounds = game.mystery?.number_of_rounds;
  const hasRound = Number.isInteger(currentRound) && currentRound > 0;
  const hasTotal = totalRounds != null && Number.isInteger(totalRounds) && totalRounds > 0;
  const finalRound = hasRound && hasTotal && currentRound >= totalRounds;
  // UI-DESIGN: preserve host advancement when the API omits a round total;
  // only a known final round switches to instructions. Block duplicate sends.
  const canAdvance = isHost && hasRound && (!hasTotal || currentRound < totalRounds!);
  const awaitingRefresh = advancedRound === currentRound;
  const handleAdvance = async () => {
    if (!canAdvance || advancing || awaitingRefresh || confirmRound !== currentRound) return;
    setError("");
    try {
      await advance({ game_id: game.objectId }).unwrap();
      setAdvancedRound(currentRound);
      setConfirmRound(null);
      await refetch().unwrap();
      if (accessCode) await refetchPlayer().unwrap();
    } catch (err) {
      setError(convertErrorMessage(err));
    }
  };

  return (
    <LightboxSurface mode="static" typeface={initialTypeface()} onUnavailable={noop}>
      <main className={styles.page}>
        {/* UI-DESIGN: one reading surface keeps the round and private actions together. */}
        <GlassPanel className={styles.surface}>
          <header className={styles.header}>
            <Link to="/open-cases" className={styles.back}>← Open cases</Link>
            <h1>{game.name}</h1>
            {game.mystery?.name && <p>{game.mystery.name}</p>}
          </header>
          <div className={styles.layout}>
            <div className={styles.mainColumn}>
              <section className={styles.roundPanel} aria-labelledby="round-heading">
                <div className={styles.roundHeading}>
                  <h2 id="round-heading" aria-live="polite">{hasRound ? `Round ${currentRound}` : "The game is underway"}</h2>
                  {hasRound && hasTotal && <span>of {totalRounds}</span>}
                </div>
                {isHost && <p>{finalRound ? "You’re in the final round. Follow the host instructions to bring the mystery to a close." : "Check the host instructions before bringing everyone into the next round."}</p>}
                <div className={styles.links}>
                  {isHost && <a href={HOST_INSTRUCTIONS_URL} target="_blank" rel="noopener noreferrer">Host instructions ↗</a>}
                  {/* UI-DESIGN: pages already check every 10 seconds, so a manual check is a quiet link, not a button */}
                  <button type="button" className={styles.quietLink} onClick={() => void refreshAll()} disabled={isFetching || checkingUpdates || advancing}>{isFetching || checkingUpdates ? "Checking…" : "Check for updates"}</button>
                </div>
                {gameRefreshFailed && <p className={styles.error} role="status">Couldn’t refresh the game. Showing the last received round. Check again when you’re connected.</p>}
                {canAdvance && (
                  <div className={styles.roundAction}>
                    {awaitingRefresh ? <p role="status">Round change sent. Refresh the game to see the latest round.</p> : confirmRound === currentRound ? (
                      <>
                        <p>Is everyone ready for round {currentRound + 1}?</p>
                        <div className={styles.confirmActions}>
                          <LightboxButton tone="primary" isDisabled={advancing} onPress={() => void handleAdvance()}>{advancing ? "Starting…" : `Start round ${currentRound + 1}`}</LightboxButton>
                          <LightboxButton tone="secondary" isDisabled={advancing} onPress={() => setConfirmRound(null)}>Not yet</LightboxButton>
                        </div>
                      </>
                    ) : <LightboxButton tone="primary" onPress={() => setConfirmRound(currentRound)}>Next round</LightboxButton>}
                  </div>
                )}
                {error && <p className={styles.error} role="alert">{error}</p>}
                {/* UI-DESIGN: 2026-10-25 updates share the round's glass so the screen reads as one surface */}
                {!hostNotPlaying && <PrivateUpdates
                  key={`${game.objectId}:${game.my_character?.objectId}:${accessCode}`}
                  updates={updates} available={Boolean(accessCode)} loading={checkingUpdates}
                  failed={updatesFailed} hasData={Boolean(playerData?.player)} retry={() => { if (accessCode) void refetchPlayer(); }}
                  emphasis={!canAdvance}
                />}
              </section>
              {hostNotPlaying ? <section className={styles.hostNote}><h2>You’re running the show</h2><p>You’re hosting without a character. Use the host instructions to guide the next round.</p></section> : (
                <CharacterReveal compact key={`${game.objectId}:${game.my_character?.objectId}:${game.my_character?.role?.objectId}`} character={game.my_character} gameId={game.objectId} />
              )}
            </div>

          </div>
        </GlassPanel>
      </main>
    </LightboxSurface>
  );
}
